// src/core/festival/festival-theme.service.ts
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, catchError, filter, from, of, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { HttpApiService } from '../authentication/services/http-api.service';
import { userApi } from '../model/constants/endpoints';
import { FestivalTheme } from './festival.config';
import { cultureSelctor } from '../../store/selectors/shared.selector';
import { FestivalActions } from '../../store/actions/festival.actions';
import { GetCultureState } from '../../store/interfaces/shared.interface';

@Injectable({ providedIn: 'root' })
export class FestivalThemeService {
  private readonly SESSION_KEY = 'oe_festival_dismissed';
  private readonly CSS_VAR_PREFIX = '--festival-';
  private readonly BODY_ACTIVE = 'festival-active';
  private readonly _activeFestival$ = new BehaviorSubject<FestivalTheme | null>(null);
  readonly activeFestival$ = this._activeFestival$.asObservable();
  constructor(
    private httpService: HttpApiService,
    private store: Store,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  // ─── Public API ──────────────────────────────────────────────────────────

  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.initFestivals();
  }

  initFestivals(): void {
    this.store
      .select(cultureSelctor)
      .pipe(
        filter((res: GetCultureState) => !!res?.culture),
        take(1)
      )
      .subscribe((res: GetCultureState) => {
        const countryCode = res.culture?.split('-')[1]?.toUpperCase();
        if (countryCode) {
          this.store.dispatch(
            FestivalActions.loadFestivalTheme({ status: 'request', countryCode })
          );
        }
      });
  }

  fetchFestivals(countryCode: string): Observable<any> {
    return from(
      this.httpService.get(
        `${userApi.getActiveFestivals}?countryCode=${countryCode}`
      )
    ).pipe(
      catchError((err) => {
        // API fail → empty data, no theme
        console.warn('[Festival] API failed:', err?.message);
        return of({ success: true, data: [] });
      })
    );
  }

  applyThemeFromData(apiResponse: any): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const festivals: FestivalTheme[] = apiResponse?.data ?? [apiResponse]

    if (!festivals.length) {
      this.clearTheme();
      return;
    }

    const active = festivals.reduce((prev, curr) =>
      Math.abs(curr.daysRemaining) < Math.abs(prev.daysRemaining) ? curr : prev
    );
    this.applyCssVariables(active);
    this.applyBodyClass(active.themeKey);
    this._activeFestival$.next(active);
  }

  applyTheme(festival: FestivalTheme): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.applyCssVariables(festival);
    this.applyBodyClass(festival.themeKey);
    this._activeFestival$.next(festival);
  }

  clearTheme(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const root = document.documentElement;
    [
      'header-banner-image', 'footer-banner-image',
      'header-icon-image', 'bg-overlay-image',
    ].forEach(v => root.style.removeProperty(`${this.CSS_VAR_PREFIX}${v}`));

    const body = document.body;
    body.classList.remove(this.BODY_ACTIVE);
    body.className = body.className
      .split(' ')
      .filter(c => !c.startsWith('festival-'))
      .join(' ')
      .trim();
    this._activeFestival$.next(null);
  }

  isBannerDismissed(themeKey: string): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    try {
      const keys: string[] = JSON.parse(
        sessionStorage.getItem(this.SESSION_KEY) ?? '[]'
      );
      return keys.includes(themeKey);
    } catch {
      return false;
    }
  }

  dismissBanner(themeKey: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const keys: string[] = JSON.parse(
        sessionStorage.getItem(this.SESSION_KEY) ?? '[]'
      );
      if (!keys.includes(themeKey)) keys.push(themeKey);
      sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(keys));
    } catch { }
  }

  getFestivalEmoji(themeKey: string): string {
    const map: Record<string, string> = {
      canada_day: '🇨🇦',
      usa_independence: '🇺🇸',
      bastille_day: '🇫🇷',
      singapore_national: '🇸🇬',
      india_independence: '🇮🇳',
      gandhi_jayanti: '🕊️',
      thanksgiving_ca: '🍂',
      diwali: '🪔',
      thanksgiving_us: '🦃',
      christmas: '🎄',
      boxing_day: '🎁',
      holi: '🌈',
    };
    return map[themeKey] ?? '🎊';
  }

  // ─── Private Helpers ─────────────────────────────────────────────────────

  private applyCssVariables(festival: FestivalTheme): void {
    const root = document.documentElement;
    // API se jo aaya wahi set karo — sab directly available hai
    const set = (cssVar: string, val: string | undefined | null) => {
      if (val?.trim()) {
        root.style.setProperty(`${this.CSS_VAR_PREFIX}${cssVar}`, val.trim());
      }
    };
    // Images
    this.setImageVar('header-banner-image', festival.headerBannerUrl);
    this.setImageVar('footer-banner-image', festival.footerBannerUrl);
    this.setImageVar('header-icon-image', festival.headerIconUrl);
    this.setImageVar('bg-overlay-image', festival.bgOverlayUrl);
    this.setImageVar('background-url', festival.backgroundUrl);
    this.setImageVar('user-theme-overlay-image', festival.userThemeOverlayUrl);
  }

  private setImageVar(cssVar: string, url: string | null | undefined): void {
    const root = document.documentElement;
    if (url) {
      root.style.setProperty(`${this.CSS_VAR_PREFIX}${cssVar}`, `url('${url}')`);
    } else {
      root.style.removeProperty(`${this.CSS_VAR_PREFIX}${cssVar}`);
    }
  }

  private applyBodyClass(themeKey: string): void {
    const body = document.body;
    body.className = body.className
      .split(' ')
      .filter(c => !c.startsWith('festival-'))
      .join(' ')
      .trim();

    body.classList.add(this.BODY_ACTIVE);
    body.classList.add(`festival-${themeKey}`);
  }
}